import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ArrowLeft, Search, X, Clock, Coffee, Navigation, List, Star, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { calculateDistance, formatDistance } from '../utils/distance';
import { 
  getCategories,
  getTypesForCategory,
  getPopularTypeIds,
  getTypeLabel,
  getCategoryLabel,
  locationHasType,
  Category,
  Type
} from '../utils/coffeeFilterMatcherV4';
import './AllSearch.css'; // Use AllSearch styles

interface CoffeeCafe {
  name: string;
  latitude: number;
  longitude: number;
  website?: string;
  coffeeHotDrinks?: any[];
  icedCoffeeTea?: any[];
  coffee_menu?: Record<string, any>;
}

interface CoffeeSearchProps {
  isOpen: boolean;
  onClose: () => void;
  cafes: CoffeeCafe[];
  allCoffeeLocations?: Record<string, any>;
  userLocation?: [number, number] | null;
  referenceLocation?: [number, number] | null; // Location from Home2
  onSelectCafe: (cafe: CoffeeCafe) => Promise<void>; // Updated to async
  onTypeFilter?: (typeId: string) => void; // Callback when a coffee type is selected
}

interface SearchResult {
  text: string;
  type: 'cafe' | 'coffee-type' | 'category';
  distance?: number;
  cafe?: CoffeeCafe;
  matchScore?: number;
  typeId?: string;
  categoryId?: string;
}

// Calculate fuzzy match score (0-1) for search relevance
const calculateMatchScore = (text: string, searchQuery: string): number => {
  const lowerText = text.toLowerCase().trim();
  const lowerSearch = searchQuery.toLowerCase().trim();

  if (!lowerSearch || lowerSearch.length === 0) return 0;
  
  // Exact match
  if (lowerText === lowerSearch) return 1.0;
  
  // Starts with search query (high relevance)
  if (lowerText.startsWith(lowerSearch)) return 0.95;
  
  // Contains search query as whole phrase (good relevance)
  if (lowerText.includes(lowerSearch)) return 0.8;

  // Word-by-word matching
  const searchWords = lowerSearch.split(/\s+/).filter((w) => w.length > 0);
  const textWords = lowerText.split(/\s+/);
  
  if (searchWords.length === 0) return 0;

  let matchedWords = 0;
  let exactWordMatches = 0;
  
  for (const searchWord of searchWords) {
    // Check for exact word match first
    if (textWords.includes(searchWord)) {
      exactWordMatches++;
      matchedWords++;
    } else {
      // Check for partial word match
      const hasPartialMatch = textWords.some((tw) => 
        tw.startsWith(searchWord) || 
        searchWord.startsWith(tw) || 
        tw.includes(searchWord) || 
        searchWord.includes(tw)
      );
      if (hasPartialMatch) {
        matchedWords++;
      }
    }
  }

  // All words matched exactly
  if (matchedWords === searchWords.length && exactWordMatches === searchWords.length) {
    return 0.7;
  }
  
  // All words matched (some partial)
  if (matchedWords === searchWords.length) {
    return 0.6;
  }
  
  // Some words matched
  if (matchedWords > 0) {
    const matchRatio = matchedWords / searchWords.length;
    const exactRatio = exactWordMatches / searchWords.length;
    // Higher score for more matches and exact matches
    return 0.3 + (matchRatio * 0.3) + (exactRatio * 0.1);
  }

  return 0;
};

// Enhanced search handler for coffee locations
const enhancedCoffeeSearchHandler = (
  search: string,
  cafes: CoffeeCafe[],
  allCoffeeLocations?: Record<string, any>,
  userLocation?: [number, number] | null
): SearchResult[] => {
  const searchLower = search.toLowerCase().trim();

  if (search.length < 2) {
    return [];
  }

  const results: SearchResult[] = [];
  const seenResults = new Set<string>();
  const MAX_RESULTS = 50;

  // Search cafes
  for (const cafe of cafes) {
    if (results.length >= MAX_RESULTS) break;

    const cafeNameLower = cafe.name.toLowerCase();
    const score = calculateMatchScore(cafe.name, search);

    // Only include results with meaningful match (at least 0.2 score)
    if (score >= 0.2 && !seenResults.has(cafe.name)) {
      seenResults.add(cafe.name);
      
      let distance: number | undefined;
      if (userLocation && cafe.latitude && cafe.longitude) {
        distance = calculateDistance(userLocation[0], userLocation[1], cafe.latitude, cafe.longitude);
      }

      results.push({
        text: cafe.name,
        type: 'cafe',
        distance,
        cafe,
        matchScore: score,
      });
    }
  }

  // Search coffee types (from all categories and popular)
  const allTypeIds = new Set<string>();
  getPopularTypeIds().forEach(id => allTypeIds.add(id));
  getCategories().forEach(category => {
    getTypesForCategory(category.id).forEach(type => allTypeIds.add(type.id));
  });

  for (const typeId of Array.from(allTypeIds)) {
    if (results.length >= MAX_RESULTS) break;

    const typeLabel = getTypeLabel(typeId);
    const typeLabelLower = typeLabel.toLowerCase();
    const score = calculateMatchScore(typeLabel, search);

    // Only include results with meaningful match (at least 0.2 score)
    if (score >= 0.2 && !seenResults.has(`type:${typeId}`)) {
      seenResults.add(`type:${typeId}`);
      results.push({
        text: typeLabel,
        type: 'coffee-type',
        matchScore: score,
        typeId,
      });
    }
  }

  // Sort results: cafes by match score first (relevance), then distance; types by match score
  results.sort((a, b) => {
    // Always prioritize category/type results at the top
    if (a.type === 'coffee-type' && b.type === 'cafe') return -1;
    if (a.type === 'cafe' && b.type === 'coffee-type') return 1;

    // For cafes, sort by match score first (most relevant), then by distance
    if (a.type === 'cafe' && b.type === 'cafe') {
      const aScore = a.matchScore || 0;
      const bScore = b.matchScore || 0;
      
      // Priority 1: Sort by match score (highest first) - most relevant results first
      if (Math.abs(aScore - bScore) > 0.01) {
        // Significant difference in match score - prioritize relevance
        return bScore - aScore;
      }
      
      // Priority 2: If match scores are similar, sort by distance
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;
      
      // Fallback to match score
      return bScore - aScore;
    }

    // For types, sort by match score
    return (b.matchScore || 0) - (a.matchScore || 0);
  });

  return results.slice(0, MAX_RESULTS);
};

const CoffeeSearch: React.FC<CoffeeSearchProps> = ({ 
  isOpen, 
  onClose, 
  cafes, 
  allCoffeeLocations,
  userLocation,
  referenceLocation,
  onSelectCafe,
  onTypeFilter
}) => {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'recent' | 'categories' | 'around-me'>('recent');
  const [viewMode, setViewMode] = useState<'recent' | 'categories' | 'popular' | 'category-detail' | 'around-me'>('recent');
  const [categoryViewMode, setCategoryViewMode] = useState<'popular' | 'category-detail'>('popular');
  const [temperatureFilter, setTemperatureFilter] = useState<'hot' | 'cold' | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [isLocating, setIsLocating] = useState(false);
  const [currentUserLocation, setCurrentUserLocation] = useState<[number, number] | null>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (recentScrollTimeoutRef.current) {
        clearTimeout(recentScrollTimeoutRef.current);
      }
    };
  }, []);

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('vibecheck_coffee_recent_searches');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setRecentSearches(Array.isArray(parsed) ? parsed : []);
      } catch (e) {
        setRecentSearches([]);
      }
    }
  }, []);

  // Get around me results - all coffee locations sorted by distance
  const getAroundMeResults = useMemo(() => {
    const activeUserLocation = currentUserLocation || userLocation || referenceLocation;
    const results: SearchResult[] = [];

    if (!activeUserLocation) return [];

    const locationsToSearch = allCoffeeLocations || {};
    const MAX_AROUND_ME_RESULTS = 50;

    for (const [name, location] of Object.entries(locationsToSearch)) {
      if (results.length >= MAX_AROUND_ME_RESULTS) break;

      if (!location || typeof location !== 'object') continue;

      const cafe = location as CoffeeCafe;
      if (cafe.latitude && cafe.longitude) {
        const distance = calculateDistance(
          activeUserLocation[0],
          activeUserLocation[1],
          cafe.latitude,
          cafe.longitude
        );

        results.push({
          text: cafe.name || name,
          type: 'cafe',
          distance,
          cafe,
          matchScore: 1.0,
        });
      }
    }

    results.sort((a, b) => {
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;
      return a.text.localeCompare(b.text);
    });

    return results;
  }, [allCoffeeLocations, currentUserLocation, userLocation, referenceLocation]);

  // Search results
  const searchResults = useMemo(() => {
    const activeUserLocation = currentUserLocation || userLocation || referenceLocation;

    // If current location is set and search is empty, show all nearby locations
    if (currentUserLocation && search.length === 0) {
      return getAroundMeResults;
    }

    if (search.length < 2) return [];
    return enhancedCoffeeSearchHandler(search, cafes, allCoffeeLocations, activeUserLocation);
  }, [search, cafes, allCoffeeLocations, userLocation, currentUserLocation, referenceLocation, getAroundMeResults]);

  // Sorted results: most relevant first (match score), then by location (distance)
  const sortedResults = useMemo(() => {
    // Priority 1: Current user location (if enabled via "Around Me")
    const currentLocation = currentUserLocation;
    // Priority 2: Reference location from Home2
    const refLocation = referenceLocation;
    
    return [...searchResults].sort((a, b) => {
      // Always prioritize category/type results at the top
      if (a.type === 'coffee-type' && b.type === 'cafe') return -1;
      if (a.type === 'cafe' && b.type === 'coffee-type') return 1;
      
      // Helper function to calculate distance from a location
      const getDistance = (result: SearchResult, fromLocation: [number, number] | null): number | null => {
        if (!fromLocation || !result.cafe || !result.cafe.latitude || !result.cafe.longitude) {
          return null;
        }
        return calculateDistance(
          fromLocation[0],
          fromLocation[1],
          result.cafe.latitude,
          result.cafe.longitude
        );
      };
      
      // For cafes: First rank by match score (relevance), then by distance
      if (a.type === 'cafe' && b.type === 'cafe') {
        const aScore = a.matchScore || 0;
        const bScore = b.matchScore || 0;

        // Priority 1: Sort by match score (highest first) - most relevant results first
        if (Math.abs(aScore - bScore) > 0.01) {
          // Significant difference in match score - prioritize relevance
          return bScore - aScore;
        }
        
        // Priority 2: If match scores are similar (within 0.01), sort by distance
        // Use current location if available, otherwise reference location
        const locationToUse = currentLocation || refLocation;
        if (locationToUse) {
          const aDist = getDistance(a, locationToUse);
          const bDist = getDistance(b, locationToUse);
          
          if (aDist !== null && bDist !== null) {
            return aDist - bDist;
          }
          if (aDist !== null) return -1;
          if (bDist !== null) return 1;
        }
        
        // If no location available, maintain match score order
        return bScore - aScore;
      }
      
      // For types, sort by match score
      return (b.matchScore || 0) - (a.matchScore || 0);
    });
  }, [searchResults, currentUserLocation, userLocation, referenceLocation]);

  const handleSelect = async (result: SearchResult) => {
    if (!result.text || typeof result.text !== 'string') return;

    // For coffee type results, trigger filter callback
    if (result.type === 'coffee-type' && result.typeId && onTypeFilter) {
      onTypeFilter(result.typeId);
      // Save to recent searches
      const updated = [result.text, ...recentSearches.filter((s) => s !== result.text)].slice(0, 10);
      setRecentSearches(updated);
      localStorage.setItem('vibecheck_coffee_recent_searches', JSON.stringify(updated));
      onClose();
      return;
    }

    // For cafe results - trigger modal like list view does
    if (result.type === 'cafe' && result.cafe) {
      // Save to recent searches
      const updated = [result.text, ...recentSearches.filter((s) => s !== result.text)].slice(0, 10);
      setRecentSearches(updated);
      localStorage.setItem('vibecheck_coffee_recent_searches', JSON.stringify(updated));
      
      // Call onSelectCafe which will trigger the modal (same as list view)
      await onSelectCafe(result.cafe);
      onClose();
    }
  };

  const handleRecentClick = async (searchTerm: string) => {
    // Check if this is a coffee type (would need to check against all types)
    const allTypeIds = new Set<string>();
    getPopularTypeIds().forEach(id => allTypeIds.add(id));
    getCategories().forEach(category => {
      getTypesForCategory(category.id).forEach(type => allTypeIds.add(type.id));
    });

    let isType = false;
    for (const typeId of Array.from(allTypeIds)) {
      if (getTypeLabel(typeId).toLowerCase() === searchTerm.toLowerCase()) {
        isType = true;
        if (onTypeFilter) {
          onTypeFilter(typeId);
          onClose();
          return;
        }
      }
    }

    // For cafes, find and select - trigger modal like list view
    const matchingCafe = cafes.find(c => c.name.toLowerCase() === searchTerm.toLowerCase());
    if (matchingCafe) {
      await onSelectCafe(matchingCafe);
    onClose();
    } else {
      // If not found, populate search
      setSearch(searchTerm);
    }
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('vibecheck_coffee_recent_searches');
  };

  // Handle scroll on mobile to dismiss keyboard and prevent accidental selections
  const handleScroll = useCallback(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      if (inputRef.current && document.activeElement === inputRef.current) {
        inputRef.current.blur();
      }
    }
    // Mark as scrolling to prevent accidental selections
    isScrollingRef.current = true;
    
    // Reset scroll flags after a delay
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 150);
  }, []);

  const handleCurrentLocation = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }

    setIsLocating(true);
    try {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location: [number, number] = [latitude, longitude];
          setIsLocating(false);
          setCurrentUserLocation(location);
          setViewMode('around-me');
          setActiveTab('around-me');
        },
        (error) => {
          setIsLocating(false);
          let errorMessage = 'Unable to get your current location.';
          if (error.code === error.PERMISSION_DENIED) {
            errorMessage = 'Location access denied. Please enable location permissions.';
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            errorMessage = 'Location information is unavailable.';
          } else if (error.code === error.TIMEOUT) {
            errorMessage = 'Location request timed out.';
          }
          alert(errorMessage);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } catch (error) {
      setIsLocating(false);
      alert('Unable to access location. Please try again.');
    }
  };

  // Update viewMode when activeTab changes
  useEffect(() => {
    setViewMode(activeTab);
  }, [activeTab]);

  // Get categories filtered by temperature (matches CoffeeMapper2)
  const getCategoriesByTemperature = (temp: 'hot' | 'cold'): Category[] => {
    const hotCategoryIds = [
      'cat_milk_coffee',
      'cat_black_coffee',
      'cat_filter_coffee',
      'cat_espresso',
      'cat_hot_chocolate',
      'cat_chai',
      'cat_tea',
      'cat_matcha',
      'cat_kids',
      'cat_wellness',
    ];
    const coldCategoryIds = ['cat_cold_coffee', 'cat_iced_drinks'];

    const categoryIds = temp === 'hot' ? hotCategoryIds : coldCategoryIds;
    return getCategories().filter((cat) => categoryIds.includes(cat.id));
  };

  // Get all cold drink types (combines types from both cold categories) - matches CoffeeMapper2
  const getAllColdTypes = (): Type[] => {
    const coldCategoryIds = ['cat_cold_coffee', 'cat_iced_drinks'];
    const allColdTypes: Type[] = [];

    coldCategoryIds.forEach((categoryId) => {
      const types = getTypesForCategory(categoryId);
      allColdTypes.push(...types);
    });

    // Sort by priority
    return allColdTypes.sort((a, b) => a.priority - b.priority);
  };

  // Handle temperature selection
  const handleTemperatureSelect = (temp: 'hot' | 'cold') => {
    setTemperatureFilter(temp);
    setActiveCategoryId(null);
  };

  // Handle mode change - reset temperature filter
  const handleCategoryModeChange = (newMode: 'popular' | 'category-detail') => {
    setCategoryViewMode(newMode);
    if (newMode === 'popular') {
      setActiveCategoryId(null);
      setTemperatureFilter(null);
    } else if (newMode === 'category-detail') {
      setActiveCategoryId(null);
      setTemperatureFilter(null);
    }
  };

  // Track touch positions to distinguish between scrolling and tapping
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const isScrollingRef = useRef<boolean>(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent, result: SearchResult) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    isScrollingRef.current = false;
    
    // Clear any existing scroll timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
  }, []);

  const handleTouchMove = useCallback(() => {
    // Mark as scrolling if touch moves
    isScrollingRef.current = true;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent, result: SearchResult) => {
    if (!touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
    const deltaTime = Date.now() - touchStartRef.current.time;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Thresholds for distinguishing tap from scroll
    const MAX_TAP_DISTANCE = 10; // pixels
    const MAX_TAP_TIME = 300; // milliseconds

    // Only trigger selection if:
    // 1. Movement is below threshold (not scrolling)
    // 2. Time is short (quick tap)
    // 3. Not currently scrolling
    if (
      distance < MAX_TAP_DISTANCE &&
      deltaTime < MAX_TAP_TIME &&
      !isScrollingRef.current
    ) {
      e.preventDefault();
      e.stopPropagation();
      handleSelect(result);
    }

    // Reset after a delay to allow scroll detection
    scrollTimeoutRef.current = setTimeout(() => {
      touchStartRef.current = null;
      isScrollingRef.current = false;
    }, 100);
  }, [handleSelect]);

  // Separate touch handlers for recent items
  const recentTouchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const recentIsScrollingRef = useRef<boolean>(false);
  const recentScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleRecentTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    recentTouchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    recentIsScrollingRef.current = false;
    
    if (recentScrollTimeoutRef.current) {
      clearTimeout(recentScrollTimeoutRef.current);
    }
  }, []);

  const handleRecentTouchMove = useCallback(() => {
    recentIsScrollingRef.current = true;
  }, []);

  const handleRecentTouchEnd = useCallback((e: React.TouchEvent, searchTerm: string) => {
    if (!recentTouchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - recentTouchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - recentTouchStartRef.current.y);
    const deltaTime = Date.now() - recentTouchStartRef.current.time;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    const MAX_TAP_DISTANCE = 10;
    const MAX_TAP_TIME = 300;

    if (
      distance < MAX_TAP_DISTANCE &&
      deltaTime < MAX_TAP_TIME &&
      !recentIsScrollingRef.current
    ) {
      e.preventDefault();
      e.stopPropagation();
      handleRecentClick(searchTerm);
    }

    recentScrollTimeoutRef.current = setTimeout(() => {
      recentTouchStartRef.current = null;
      recentIsScrollingRef.current = false;
    }, 100);
  }, [handleRecentClick]);

  // Cleanup recent touch timeout on unmount
  useEffect(() => {
    return () => {
      if (recentScrollTimeoutRef.current) {
        clearTimeout(recentScrollTimeoutRef.current);
      }
    };
  }, []);

  const renderResultItem = (result: SearchResult, index: number) => {
    const { text, type, distance } = result;

    return (
      <motion.div
        key={`result-${index}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.02 }}
        className="allsearch-result-item"
        onClick={(e) => {
          // Only handle click if not from a touch event (desktop)
          if (!touchStartRef.current) {
            e.preventDefault();
            e.stopPropagation();
            handleSelect(result);
          }
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
          handleTouchStart(e, result);
        }}
        onTouchMove={(e) => {
          handleTouchMove();
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
          handleTouchEnd(e, result);
        }}
      >
        <div className="allsearch-result-icon">
          {type === 'coffee-type' ? <Coffee size={20} /> : <Coffee size={20} />}
        </div>
        <div className="allsearch-result-content">
          <h3 className="allsearch-result-name">{text}</h3>
          {type === 'coffee-type' && (
            <p className="allsearch-result-subtitle">Coffee Type</p>
          )}
        </div>
        <div className="allsearch-result-right">
          {distance !== undefined && (
            <span className="allsearch-result-distance">{formatDistance(distance)}</span>
          )}
        </div>
      </motion.div>
    );
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="coffee-search-modal"
        initial={{ x: '100%', opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="allsearch-view"
          >
            {/* Header */}
      <div className="allsearch-header">
        <button className="allsearch-back-button" onClick={onClose}>
          <ArrowLeft size={24} />
              </button>
        <div className="allsearch-search-container">
          <div className="allsearch-search-bar">
            <Search size={20} className="allsearch-search-icon" />
                  <input
                    ref={inputRef}
                    type="text"
              placeholder="Search coffee locations..."
              value={search || ''}
                    onChange={(e) => setSearch(e.target.value)}
              className="allsearch-search-input"
              autoFocus
              size={1}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                  />
            {search.length > 0 && (
                    <button
                onClick={() => {
                  setSearch('');
                  setViewMode(activeTab);
                }}
                className="allsearch-clear-button"
                type="button"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>

      {/* Tabs - Only show when search is empty */}
      {search.length === 0 && (
        <div className="allsearch-tabs">
          <button
            className={`allsearch-tab ${activeTab === 'recent' ? 'active' : ''}`}
            onClick={() => setActiveTab('recent')}
          >
            Recent
          </button>
          <button
            className={`allsearch-tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categories
          </button>
          <button
            className={`allsearch-tab ${activeTab === 'around-me' ? 'active' : ''}`}
            onClick={() => {
              if (currentUserLocation) {
                setActiveTab('around-me');
              } else {
                handleCurrentLocation();
              }
            }}
            disabled={isLocating}
          >
            {isLocating ? 'Locating...' : 'Around Me'}
          </button>
        </div>
      )}

            {/* Content */}
      <div 
        className={`allsearch-content ${search.length > 0 ? 'allsearch-content-has-results' : ''}`}
        ref={contentRef}
        onScroll={handleScroll}
        onTouchMove={handleScroll}
      >
        {search.length === 0 ? (
          <>
            {/* Recent Searches View */}
            {viewMode === 'recent' && (
              <div className="allsearch-view-content">
                <div className="allsearch-section-header">
                  <Clock size={18} className="allsearch-section-icon" />
                  <h2 className="allsearch-section-title">Recent Searches</h2>
                  {recentSearches.length > 0 && (
                    <button className="allsearch-clear-recent" onClick={clearRecentSearches}>
                      clear
                    </button>
                  )}
                </div>
                {recentSearches.length > 0 ? (
                  <div className="allsearch-recent-list">
                    {recentSearches.map((recentSearch, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.02 }}
                        className="allsearch-recent-item"
                        onClick={(e) => {
                          // Only handle click if not from a touch event (desktop)
                          if (!recentTouchStartRef.current) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRecentClick(recentSearch);
                          }
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          handleRecentTouchStart(e);
                        }}
                        onTouchMove={(e) => {
                          handleRecentTouchMove();
                        }}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          handleRecentTouchEnd(e, recentSearch);
                        }}
                      >
                        <div className="allsearch-recent-thumbnail">
                          <Coffee size={24} />
                        </div>
                        <div className="allsearch-recent-content">
                          <h3 className="allsearch-recent-name">{recentSearch}</h3>
                          <p className="allsearch-recent-date">Recently searched</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="allsearch-empty">
                    <p>No recent searches</p>
                    <p className="allsearch-empty-hint">Start searching to see your history here</p>
                  </div>
                )}
              </div>
            )}

            {/* Categories View */}
            {viewMode === 'categories' && (
              <div className="allsearch-view-content">
                <div className="allsearch-section-header">
                  <List size={18} className="allsearch-section-icon" />
                  <h2 className="allsearch-section-title">Coffee Types</h2>
                </div>
                
                {/* Mode Switcher */}
                {!activeCategoryId && (
                  <div 
                    className="coffee-filter-mode-switcher" 
                    style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: '12px', 
                      marginBottom: '20px',
                      width: '100%'
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleCategoryModeChange('popular')}
                      className={`coffee-filter-mode-button ${categoryViewMode === 'popular' ? 'active' : ''}`}
                      style={{ 
                        padding: '14px 20px',
                        background: categoryViewMode === 'popular' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                        border: `1.5px solid ${categoryViewMode === 'popular' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                        borderRadius: '12px',
                        color: categoryViewMode === 'popular' ? '#7c3aed' : '#111827',
                        fontSize: '0.9375rem',
                        fontWeight: categoryViewMode === 'popular' ? 600 : 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      <span>Popular</span>
                      {categoryViewMode === 'popular' && <Star size={16} style={{ color: '#7c3aed' }} />}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCategoryModeChange('category-detail')}
                      className={`coffee-filter-mode-button ${categoryViewMode === 'category-detail' ? 'active' : ''}`}
                      style={{ 
                        padding: '14px 20px',
                        background: categoryViewMode === 'category-detail' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                        border: `1.5px solid ${categoryViewMode === 'category-detail' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                        borderRadius: '12px',
                        color: categoryViewMode === 'category-detail' ? '#7c3aed' : '#111827',
                        fontSize: '0.9375rem',
                        fontWeight: categoryViewMode === 'category-detail' ? 600 : 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      <span>Categories</span>
                      {categoryViewMode === 'category-detail' && <List size={16} style={{ color: '#7c3aed' }} />}
                    </button>
                  </div>
                )}

                {/* Popular Types */}
                {categoryViewMode === 'popular' && (
                  <div className="allsearch-categories-grid">
                    {getPopularTypeIds().map((typeId) => (
                      <button
                        key={typeId}
                        className="allsearch-category-item"
                        onClick={() => {
                          if (onTypeFilter) {
                            onTypeFilter(typeId);
                            const typeLabel = getTypeLabel(typeId);
                            const updated = [typeLabel, ...recentSearches.filter((s) => s !== typeLabel)].slice(0, 10);
                            setRecentSearches(updated);
                            localStorage.setItem('vibecheck_coffee_recent_searches', JSON.stringify(updated));
                            onClose();
                          }
                        }}
                      >
                        <Coffee size={24} className="allsearch-category-icon" />
                        <span className="allsearch-category-name">{getTypeLabel(typeId)}</span>
                      </button>
                    ))}
                      </div>
                )}

                {/* Temperature Selection (only in categories mode, when no temperature selected) */}
                {categoryViewMode === 'category-detail' && temperatureFilter === null && (
                  <div className="coffee-filter-temperature">
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: 'rgba(17, 24, 39, 0.6)',
                        marginBottom: '16px',
                        textAlign: 'center',
                        fontWeight: 500,
                      }}
                    >
                      Select a drink type to continue
                    </div>
                    <div className="allsearch-categories-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <button
                        onClick={() => handleTemperatureSelect('hot')}
                        className="coffee-filter-card coffee-filter-temperature-card"
                        style={{
                          padding: '24px 20px',
                          fontSize: '1rem',
                          fontWeight: 600,
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <span className="coffee-filter-card-name">Coffee / Hot Drinks</span>
                        <ChevronDown 
                          size={20} 
                          style={{ 
                            color: 'rgba(17, 24, 39, 0.4)',
                            transform: 'rotate(-90deg)',
                            marginTop: '4px'
                          }} 
                        />
                      </button>
                      <button
                        onClick={() => handleTemperatureSelect('cold')}
                        className="coffee-filter-card coffee-filter-temperature-card"
                        style={{
                          padding: '24px 20px',
                          fontSize: '1rem',
                          fontWeight: 600,
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <span className="coffee-filter-card-name">Cold / Iced Drinks</span>
                        <ChevronDown 
                          size={20} 
                          style={{ 
                            color: 'rgba(17, 24, 39, 0.4)',
                            transform: 'rotate(-90deg)',
                            marginTop: '4px'
                          }} 
                        />
                      </button>
                    </div>
                  </div>
                )}

                {/* Hot Categories (only for hot drinks in categories mode, when temperature is selected) */}
                {categoryViewMode === 'category-detail' && temperatureFilter === 'hot' && !activeCategoryId && (
                  <div className="coffee-filter-categories">
                    <button
                      onClick={() => setTemperatureFilter(null)}
                      className="allsearch-back-to-categories"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        background: 'rgba(0, 0, 0, 0.05)',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        marginBottom: '16px',
                        fontSize: '0.875rem',
                        color: 'rgba(17, 24, 39, 0.7)',
                      }}
                    >
                      <ArrowLeft size={16} />
                      <span>Back</span>
                    </button>
                    <div className="allsearch-categories-grid">
                      {getCategoriesByTemperature('hot').map((category) => (
                        <button
                          key={category.id}
                          className="allsearch-category-item"
                          onClick={() => setActiveCategoryId(category.id)}
                        >
                          <Coffee size={24} className="allsearch-category-icon" />
                          <span className="allsearch-category-name">{category.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cold Types (combines types from both cold categories) */}
                {categoryViewMode === 'category-detail' && temperatureFilter === 'cold' && !activeCategoryId && (
                  <div className="coffee-filter-categories">
                    <button
                      onClick={() => setTemperatureFilter(null)}
                      className="allsearch-back-to-categories"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        background: 'rgba(0, 0, 0, 0.05)',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        marginBottom: '16px',
                        fontSize: '0.875rem',
                        color: 'rgba(17, 24, 39, 0.7)',
                      }}
                    >
                      <ArrowLeft size={16} />
                      <span>Back</span>
                    </button>
                    <div className="allsearch-categories-grid">
                      {getAllColdTypes().map((type) => (
                        <button
                          key={type.id}
                          className="allsearch-category-item"
                          onClick={() => {
                            if (onTypeFilter) {
                              onTypeFilter(type.id);
                              const updated = [type.label, ...recentSearches.filter((s) => s !== type.label)].slice(0, 10);
                              setRecentSearches(updated);
                              localStorage.setItem('vibecheck_coffee_recent_searches', JSON.stringify(updated));
                              onClose();
                            }
                          }}
                        >
                          <Coffee size={24} className="allsearch-category-icon" />
                          <span className="allsearch-category-name">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Types for Selected Category (only for hot drinks) */}
                {categoryViewMode === 'category-detail' && temperatureFilter === 'hot' && activeCategoryId && (
                  <>
                    <button
                      onClick={() => setActiveCategoryId(null)}
                      className="allsearch-back-to-categories"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        background: 'rgba(0, 0, 0, 0.05)',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        marginBottom: '16px',
                        fontSize: '0.875rem',
                        color: 'rgba(17, 24, 39, 0.7)',
                      }}
                    >
                      <ArrowLeft size={16} />
                      <span>Back</span>
                    </button>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: 'rgba(17, 24, 39, 0.6)', 
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {getCategoryLabel(activeCategoryId)}
                    </div>
                    <div className="allsearch-categories-grid">
                      {getTypesForCategory(activeCategoryId).map((type) => (
                        <button
                          key={type.id}
                          className="allsearch-category-item"
                          onClick={() => {
                            if (onTypeFilter) {
                              onTypeFilter(type.id);
                              const updated = [type.label, ...recentSearches.filter((s) => s !== type.label)].slice(0, 10);
                              setRecentSearches(updated);
                              localStorage.setItem('vibecheck_coffee_recent_searches', JSON.stringify(updated));
                              onClose();
                            }
                          }}
                        >
                          <Coffee size={24} className="allsearch-category-icon" />
                          <span className="allsearch-category-name">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Around Me View */}
            {viewMode === 'around-me' && (
              <div className="allsearch-view-content">
                <div className="allsearch-section-header">
                  <Navigation size={18} className="allsearch-section-icon" />
                  <h2 className="allsearch-section-title">Coffee Locations Near You</h2>
                </div>
                {isLocating ? (
                  <div className="allsearch-empty">
                    <div className="allsearch-spinner" />
                    <p>Getting your location...</p>
                  </div>
                ) : getAroundMeResults.length > 0 ? (
                  <div className="allsearch-results">
                    {getAroundMeResults.map((result, index) => renderResultItem(result, index))}
                  </div>
                ) : currentUserLocation ? (
                  <div className="allsearch-empty">
                    <p>No coffee locations found nearby</p>
                    <p className="allsearch-empty-hint">Make sure location permissions are enabled</p>
                  </div>
                ) : (
                  <div className="allsearch-empty">
                    <p>Click "Around Me" to find coffee locations near you</p>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          /* Search Results */
          <div className="allsearch-results">
            {sortedResults.length === 0 ? (
              <div className="allsearch-empty">
                <p>No results found for "{search}"</p>
              </div>
            ) : (
              sortedResults.map((result, index) => renderResultItem(result, index))
            )}
                </div>
              )}
            </div>
          </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default CoffeeSearch;
